import React, { useState, useRef, useEffect } from "react";
import {
  Check,
  CircleCheck,
  CircleX,
  CircleHelp,
  RotateCcw,
  Trash2,
  Loader2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Textarea } from "@/components/ui/textarea";

interface FreeResponseQuestionProps {
  question: {
    id: string;
    prompt: string;
    correct_answer: { value?: string } | null;
    explanation: string | null;
  };
  state: "unanswered" | "correct" | "incorrect" | "skipped";
  onSubmit: (userAnswer: string) => void;
  onDontKnow: () => void;
  onRemove: () => void;
  isGrading?: boolean;
}

export const FreeResponseQuestion: React.FC<FreeResponseQuestionProps> = ({
  question,
  state,
  onSubmit,
  onDontKnow,
  onRemove,
  isGrading = false,
}) => {
  const [value, setValue] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Focus textarea when mounted
  useEffect(() => {
    if (state === "unanswered" && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [state]);

  // Reset value when question changes
  useEffect(() => {
    setValue("");
  }, [question.id]);

  const handleSubmit = () => {
    if (!value.trim() || isGrading) return;
    onSubmit(value.trim());
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Submit on Ctrl+Enter or Cmd+Enter
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  };

  const correctAnswerObj = question.correct_answer as { value?: string; keywords?: string[]; expected_keywords?: string[] } | null;
  const correctAnswer = correctAnswerObj?.value ?? "";
  const keywords = correctAnswerObj?.keywords || correctAnswerObj?.expected_keywords || [];

  return (
    <div className="space-y-4">
      {/* Question prompt + trash */}
      <div className="flex items-start justify-between gap-2">
        <h2 className="text-base font-normal leading-7">{question.prompt}</h2>
        <button
          type="button"
          onClick={onRemove}
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-destructive"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      {/* Free response input - only shown in unanswered state */}
      {state === "unanswered" && (
        <>
          <Textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type your answer here..."
            className="min-h-[120px] resize-none rounded-xl border-border text-base focus-visible:ring-primary"
            disabled={isGrading}
          />
          <p className="text-xs text-muted-foreground">
            Press Ctrl+Enter to submit
          </p>
        </>
      )}

      {/* User's submitted answer when answered */}
      {state !== "unanswered" && (
        <div
          className={cn(
            "rounded-xl border-2 p-3",
            state === "correct" && "border-correct bg-correct/10",
            state === "incorrect" && "border-incorrect bg-incorrect/10",
            state === "skipped" && "border-warning bg-warning/10"
          )}
        >
          <p className="text-xs font-medium text-muted-foreground mb-1">
            Your answer:
          </p>
          <p className="text-base">
            {state === "skipped" ? (
              <span className="italic text-muted-foreground">No answer provided</span>
            ) : (
              value || <span className="italic text-muted-foreground">No answer provided</span>
            )}
          </p>
        </div>
      )}

      {/* Correct answer shown for incorrect/skipped */}
      {(state === "incorrect" || state === "skipped") && (correctAnswer || keywords.length > 0) && (
        <div className="rounded-xl border-2 border-dashed border-correct p-3">
          <p className="text-xs font-medium text-correct mb-1">
            Correct answer:
          </p>
          {correctAnswer && (
              <p className="text-base text-correct">
              {correctAnswer}
            </p>
          )}
          {keywords.length > 0 && !correctAnswer && (
            <p className="text-base text-correct">
              Key concepts: {keywords.join(", ")}
            </p>
          )}
        </div>
      )}

      {/* Feedback explanation */}
      {state !== "unanswered" && question.explanation && (
        <div
          className={cn(
            "rounded-lg p-4 text-sm leading-normal",
            state === "correct" && "bg-correct/10 text-correct",
            state === "incorrect" && "bg-incorrect/10 text-incorrect",
            state === "skipped" && "bg-warning/10 text-warning"
          )}
        >
          <div className="mb-2 flex items-center gap-2 font-medium">
            {state === "correct" && (
              <>
                <CircleCheck className="h-5 w-5" />
                <span>Correct</span>
              </>
            )}
            {state === "incorrect" && (
              <>
                <CircleX className="h-5 w-5" />
                <span>Incorrect</span>
              </>
            )}
            {state === "skipped" && (
              <>
                <CircleHelp className="h-5 w-5" />
                <span>Don't know</span>
              </>
            )}
          </div>
          <p>{question.explanation}</p>
        </div>
      )}

      {/* Action buttons - only for unanswered state */}
      {state === "unanswered" && (
        <div className="flex items-center gap-3 pt-2">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!value.trim() || isGrading}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isGrading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Grading...
              </>
            ) : (
              <>
                <Check className="h-4 w-4" />
                Submit Answer
              </>
            )}
          </button>

          <button
            type="button"
            onClick={onDontKnow}
            disabled={isGrading}
            className="inline-flex items-center justify-center rounded-lg border border-input bg-background px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground disabled:cursor-not-allowed disabled:opacity-50"
          >
            Don't know
          </button>
        </div>
      )}
    </div>
  );
};
